import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import EditIcon from "../../../components/icons/EditIcon";
import PromptModal from "../../../components/modals/PromptModal";
import { colors } from "../../../config/theme";
import { Chip } from "../../../helpers/general";
import { tdStyle, thStyle } from "../../../helpers/constants";

interface TaxiSpecialZonesTableProps {
  specialZones: RideSpecialZone[];
  deleteSpecialZone: (id: string) => void;
  isDeleting: boolean;
}

interface TableBodyProps {
  specialZone: RideSpecialZone;
  onDelete: () => void;
}

function TableBodyRowContent({ specialZone, onDelete }: TableBodyProps) {
  return (
    <CTableRow>
      <CTableDataCell style={tdStyle}>
        <span className="fw-semibold text-capitalize">{specialZone.name}</span>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <span className="fw-bold" style={{ fontSize: 14 }}>
          {specialZone.price}
          <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>
            MAD
          </span>
        </span>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <Chip tone={specialZone.isActive ? "success" : "danger"}>
          {specialZone.isActive ? "✓ Active" : "✕ Inactive"}
        </Chip>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {specialZone.description ? (
          <div
            className="text-truncate"
            style={{ maxWidth: 280 }}
            title={specialZone.description}
          >
            {specialZone.description}
          </div>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <Link
            to={`/taxi/special-zones/update/${specialZone.id}`}
            className="btn btn-sm rounded-pill border px-3"
            title="Modifier"
          >
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </Link>
          <button
            type="button"
            onClick={onDelete}
            className="btn btn-sm btn-outline-danger rounded-pill px-3"
            title="Supprimer"
          >
            <DeleteIcon width="16" height="16" />
          </button>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

export default function TaxiSpecialZonesTable({
  specialZones,
  deleteSpecialZone,
  isDeleting,
}: TaxiSpecialZonesTableProps) {
  const [zoneToDelete, setZoneToDelete] = useState<RideSpecialZone | null>(
    null,
  );

  useEffect(() => {
    if (!isDeleting) setZoneToDelete(null);
  }, [isDeleting]);

  return (
    <>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" style={thStyle}>
              Nom
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Prix
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Statut
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Description
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle} />
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {specialZones?.map((zone) => (
            <TableBodyRowContent
              key={zone.id}
              specialZone={zone}
              onDelete={() => setZoneToDelete(zone)}
            />
          ))}
        </CTableBody>
      </CTable>

      <PromptModal
        open={zoneToDelete !== null}
        onAccept={() => deleteSpecialZone(zoneToDelete?.id!)}
        onClose={() => setZoneToDelete(null)}
        loading={isDeleting}
      />
    </>
  );
}
